import { Logo } from "@components/atoms/Logo"
import { ResponsiveImage } from "@components/atoms/ResponsiveImage"
import { THEMES } from "@components/atoms/Themed"
import { Button } from "@components/ui/Button"
import { FlyingCarpetTab } from "@features/flying-carpet/carpet-panels"
import { useFlyingCarpet } from "@features/flying-carpet/useFlyingCarpet"
import { useAdminExpandedSidebar } from "@hooks/useAdminExpandedSidebar"
import { defineBlock } from "eddev/blocks"
import { Link, useRoute, useRouter } from "eddev/routing"
import { act, Fragment, PropsWithChildren, useEffect, useRef, useState } from "react"
import { twMerge } from "tailwind-merge"
import { Slot } from "@radix-ui/react-slot"
import { MenuProvider, useMenuContext } from "../../contexts/MenuContext"
import { Badge } from "@components/ui/Badge"
import { useCart } from "@features/ticketing/useCart"
import { cn } from "@utils/tw"
import { useSquadup } from "@features/ticketing/useSquadUp"

export const meta: BlockMeta = {
  title: "Header Menu",
  inserter: false,
  tags: [],
}

const MenuToggle = ({ className, onToggle }: { className?: string; onToggle: () => void }) => {
  const { open, setOpen } = useMenuContext()

  return (
    <div
      onClick={() => onToggle()}
      className={twMerge(
        "pointer-events-auto group cursor-pointer rounded-md flex justify-center items-center shrink-0 p-4",
        open ? "open" : "",
        className,
      )}
    >
      <div className="flex flex-col justify-center items-center w-navToggleW h-navToggleH gap-navToggleGap">
        <div className="w-navToggleW h-navToggleLineH bg-current transition-all group-[.open]:rotate-45 group-[.open]:translate-y-navToggleAnimate rounded-md" />
        <div className="w-navToggleW h-navToggleLineH bg-current transition-all group-[.open]:opacity-0 rounded-md" />
        <div className="w-navToggleW h-navToggleLineH bg-current transition-all group-[.open]:-rotate-45 group-[.open]:-translate-y-navToggleAnimate rounded-md" />
      </div>
    </div>
  )
}

export default defineBlock("header/menu", (props) => {
  const { open, setOpen } = useMenuContext()
  const [activeCategory, setActiveCategory] = useState<string | null>(null)
  const categoryTimer = useRef<number | null>(null)
  const [searchOpen, setSearchOpen] = useState(false)
  const isOnSeason = props.miscSettings?.festivalSettings?.season === "on"
  const router = useRouter()
  const route = useRoute()
  const isSearchPage = route.pathname === "/search"
  const searchInputRef = useRef<HTMLInputElement>(null)
  const initialSearchValue = route.query.search as string | undefined
  const searchTimer = useRef<number | null>(null)

  const trySearch = (v: string) => {
    if (searchTimer.current) window.clearTimeout(searchTimer.current)
    searchTimer.current = window.setTimeout(() => {
      router.navigate(`/search?search=${v}`)
    }, 500)
  }

  // when using the admin panel, if editing this block, add expanded sidebar class
  const { setAdminExpandedSidebar } = useAdminExpandedSidebar()
  useEffect(() => {
    setAdminExpandedSidebar(true)
    return () => {
      setAdminExpandedSidebar(false)
    }
  }, [])

  useEffect(() => {
    setSearchOpen(false)
  }, [route.pathname])

  const setCategory = (category: string | null, immediate?: boolean) => {
    if (categoryTimer.current) window.clearTimeout(categoryTimer.current)
    if (immediate || (category && category !== "")) {
      setActiveCategory(category)
    } else {
      categoryTimer.current = window.setTimeout(() => {
        setActiveCategory(null)
      }, 350)
    }
  }

  const onToggle = () => {
    if (open) setCategory(null, true)
    setOpen(!open)
  }

  return (
    <div
      className={twMerge(
        "w-full h-headerTopHeight relative z-50 pointer-events-auto",
        env.admin && "h-[100vh] flex items-center justify-center",
      )}
    >
      <div className={twMerge("w-full fixed left-0 top-0 z-50")}>
        <div className="w-full h-headerTopHeight flex items-center justify-between relative shadow-header bg-white z-50">
          <div className="h-full flex items-center gap-4">
            <Link href="/" className="h-full flex items-center">
              <Logo />
            </Link>

            <ul className="hidden md:flex items-center h-full">
              {props.categories?.map(
                (category, i) =>
                  category && (
                    <div
                      key={i}
                      className={twMerge(
                        "px-2 group cursor-pointer h-full flex items-center",
                        activeCategory === category.title && "active",
                      )}
                      onMouseOver={() => setCategory(category.title)}
                      onMouseOut={() => setCategory(null)}
                    >
                      <li className="px-2 py-1 relative type-label-m mt-[0.2em]">
                        {category.title}
                        <span className="inline-block absolute bottom-0 left-0 w-full h-[0.1em] bg-current origin-center scale-x-0 transition-transform duration-200 group-hover:scale-x-100 group-[&.active]:scale-x-100" />
                      </li>
                    </div>
                  ),
              )}
            </ul>
          </div>

          <div className="h-full flex items-center gap-1 pr-3 md:pr-4">
            <Button
              variant="outline"
              href={props.ticketingLink?.url}
              target={props.ticketingLink?.target || undefined}
              className="mr-4 md:mr-5"
            >
              {props.ticketingLink?.title || "Ticketing"}
            </Button>
            <Button
              variant="ghost"
              icon="search"
              onClick={() => {
                if (!searchOpen) {
                  if (searchInputRef.current) {
                    searchInputRef.current.value = ""
                    searchInputRef.current.focus()
                  }
                }
                setSearchOpen(!searchOpen)
              }}
            />
            <CartButton />
            <CarpetTrigger tab="account">
              <Button variant="ghost" icon="account" onClick={() => setSearchOpen(false)} />
            </CarpetTrigger>
            <MenuToggle className="md:hidden" onToggle={onToggle} />
          </div>
        </div>

        {/* Search */}
        <div className="w-full absolute left-0 top-headerTopHeight h-[100vh] overflow-hidden pointer-events-none">
          <div
            className={twMerge(
              "w-full bg-white shadow-header absolute left-0 top-0 h-headerSearch flex items-center justify-between pointer-events-none opacity-0 -translate-y-3 transition-all duration-300",
              (searchOpen || isSearchPage) && "opacity-100 -translate-y-0 pointer-events-auto",
            )}
          >
            <input
              ref={searchInputRef}
              type="text"
              className="block w-full h-full px-5 outline-none"
              placeholder="Search for films, events, guests..."
              onChange={(v) => trySearch(v.currentTarget.value)}
              defaultValue={initialSearchValue}
            />
            {!isSearchPage && (
              <div
                className="shrink-0 cursor-pointer flex items-center justify-center h-full px-5"
                onClick={() => setSearchOpen(false)}
              >
                <svg className="w-6 h-6" viewBox="0 0 31 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <rect
                    x="11.6108"
                    y="6.44678"
                    width="13"
                    height="2"
                    transform="rotate(45 11.6108 6.44678)"
                    fill="black"
                  />
                  <rect
                    x="20.8032"
                    y="7.86084"
                    width="13"
                    height="2"
                    transform="rotate(135 20.8032 7.86084)"
                    fill="black"
                  />
                </svg>
              </div>
            )}
          </div>
        </div>

        {/* Nav drawers */}
        <div className="w-full absolute left-0 top-headerTopHeight h-[100vh] overflow-hidden pointer-events-none">
          <div
            className={cn(
              "fixed h-[calc(100%-var(--spacing-headerTopHeight))] w-full overflow-auto md:relative md:h-auto md:overflow-visible",
              open && "pointer-events-auto md:pointer-events-none",
            )}
          >
            <div
              className={cn("w-full h-full absolute left-0 top-0 md:hidden", open && "pointer-events-auto")}
              onTouchEnd={() => {
                setCategory(null, true)
                setOpen(false)
              }}
            />
            {props.categories?.map((category, i) => {
              const shown = []
              return (
                category && (
                  <div className="w-full" key={i}>
                    <div
                      key={i}
                      className={twMerge(
                        "w-full flex md:hidden items-center relative z-10 opacity-0 -translate-y-6 transition-all duration-300",
                        activeCategory === category.title && "active",
                        open && "pointer-events-auto opacity-100 translate-y-0",
                      )}
                      onClick={() => setCategory(activeCategory === category.title ? null : category.title, true)}
                    >
                      <Button
                        variant="ghost"
                        size="lg"
                        className={cn(
                          "w-full bg-grey8 hover:bg-grey8 justify-start px-7 gap-3",
                          activeCategory === category.title && "!bg-accent",
                        )}
                        rightIcon={activeCategory === category.title ? "chevronUp" : "chevronDown"}
                      >
                        {category.title}
                      </Button>
                    </div>
                    <div className="w-full bg-grey8 md:bg-transparent">
                      <div
                        className={twMerge(
                          "h-0 md:h-auto overflow-hidden md:overflow-visible relative md:absolute top-0 left-0 w-full bg-black text-white shadow-header md:grid grid-cols-1 md:grid-cols-3 pointer-events-none opacity-0 -translate-y-6 transition-all duration-300",
                          activeCategory === category.title &&
                            "grid opacity-100 translate-y-0 pointer-events-auto h-auto",
                        )}
                        onMouseOver={() => setCategory(category.title)}
                        onMouseOut={() => setCategory(null)}
                      >
                        {category.submenus?.map((submenu, j) => {
                          // handle special logic
                          const programOn = isOnSeason && submenu?.specialLogic === "program_on"
                          const programOff = !isOnSeason && submenu?.specialLogic === "program_off"
                          const show = submenu?.specialLogic === "none" || programOn || programOff
                          const themed = category.theme && category.theme !== "none" && THEMES[category.theme]
                          if (show) shown.push(j)

                          return (
                            submenu &&
                            show && (
                              <div
                                key={j}
                                className={twMerge(
                                  "col-span-1 p-7 md:p-6 flex",
                                  shown.length === 1 && themed && [THEMES[category.theme!].bg, "text-black"],
                                )}
                              >
                                <div className="relative flex flex-col gap-5 md:gap-5 w-full">
                                  <p className="type-label-m-light">{submenu.title}</p>
                                  <ul className="flex flex-col gap-5 md:gap-5">
                                    {submenu.links?.map((item, k) => {
                                      const showTheme = shown.length === 3 && themed
                                      return (
                                        item && (
                                          <Link
                                            key={k}
                                            href={item.link?.url}
                                            target={item.link?.target || undefined}
                                            className={twMerge(
                                              "flex flex-col gap-2 no-underline",
                                              programOn && shown.length === 1 && k === submenu.links!.length - 1
                                                ? "md:!absolute md:bottom-0 md:left-0 type-title-s"
                                                : shown.length === 1
                                                  ? "type-title-xl"
                                                  : "type-title-s",
                                              showTheme && [THEMES[category.theme!].text],
                                            )}
                                            onClick={() => {
                                              setCategory(null, true)
                                              setOpen(false)
                                            }}
                                          >
                                            <span className="hover-link">
                                              {item.link?.title?.replaceAll("&amp;", "&")}
                                            </span>
                                            {item.subText && (
                                              <span className={cn("type-body-m", shown.length >= 2 && "text-white")}>
                                                {item.subText}
                                              </span>
                                            )}
                                          </Link>
                                        )
                                      )
                                    })}
                                  </ul>
                                </div>
                              </div>
                            )
                          )
                        })}

                        {/* featured content */}
                        {category.featuredContent &&
                          category.featuredContent.map((feature, j) => {
                            const Linked = feature?.link?.url ? Link : "div"
                            const LinkedProps = feature?.link?.url
                              ? { href: feature.link.url, target: feature.link.target || undefined }
                              : {}
                            return (
                              j === 0 && (
                                <Linked key={j} {...LinkedProps} className="col-span-1 p-8 md:p-6 flex">
                                  <ResponsiveImage {...feature?.image} className="w-full" />
                                </Linked>
                              )
                            )
                          })}
                      </div>
                    </div>
                  </div>
                )
              )
            })}
          </div>
        </div>
      </div>
    </div>
  )
})

function CarpetTrigger(
  props: PropsWithChildren<{
    tab: FlyingCarpetTab
  }>,
) {
  const carpet = useFlyingCarpet()
  return (
    <Slot
      data-state={carpet.isOpen && carpet.tab?.startsWith(props.tab) ? "open" : "closed"}
      onClick={() => {
        carpet.launch(props.tab)
      }}
      id={"flying-carpet-anchor-" + props.tab}
      className={carpet.isOpen ? "pointer-events-auto" : ""}
    >
      {props.children}
    </Slot>
  )
}

function CartButton() {
  const squadup = useSquadup()
  const cartTotal = useCart().quantity

  return (
    <Badge label={cartTotal} enabled={cartTotal > 0}>
      <Button
        variant="ghost"
        icon="cart"
        onClick={() => {
          squadup.open({ showCheckout: true })
        }}
      />
    </Badge>
  )
}
